// packages/web-ui/src/state/close-session-tabs.ts
//
// Bridges a chat-session deletion to the navigation slice's tab list. The
// sessions store is a standalone singleton (see `state/sessions.ts`), so when
// a session is hard-deleted the matching `chat/<sessionId>` tab in the root
// store must be closed explicitly — otherwise a removed conversation leaves an
// orphaned tab behind.

import { useRootStore } from './store.js';
import { buildTabId } from './navigation.js';
import { navigateTo } from '../router/hash-router.js';

/**
 * Close the `chat/<sessionId>` tab for each deleted session, then reconcile the
 * URL hash and the chat slice's `activeSessionId` from the real end-state.
 *
 * Why the explicit hash reconciliation: `closeTab` re-syncs `window.location`
 * on each call, but in a multi-close (delete-all) an intermediate `closeTab`
 * can leave the hash aimed at a tab a later iteration then closes — and closing
 * the *only* tab never updates the hash at all. A stale hash later replays
 * through the hash-router's `setRoute` (AppShell's `hashchange` effect) and
 * resurrects a ghost tab for the just-deleted session (its transcript 404s).
 * So after the close loop we point the hash at the genuinely-surviving active
 * tab, or — when none survive — at the unparameterised `/chat` route, which a
 * deleted session can never reopen.
 */
export function closeDeletedSessionTabs(sessionIds: readonly string[]): void {
  if (sessionIds.length === 0) return;
  const store = useRootStore.getState();
  const activeSessionDeleted =
    store.activeSessionId !== null && sessionIds.includes(store.activeSessionId);

  for (const sessionId of sessionIds) {
    store.closeTab(buildTabId('chat', sessionId));
  }

  const next = useRootStore.getState();
  const activeTab =
    next.activeTabId === null
      ? null
      : next.openTabs.find((t) => t.id === next.activeTabId) ?? null;

  if (activeTab === null) {
    navigateTo({ kind: 'chat' });
  } else {
    // Re-point the hash at the genuinely-active tab (no-op if already correct).
    next.setActiveTab(activeTab.id);
  }

  // Keep the chat slice's `activeSessionId` aligned with the surviving tab so a
  // dangling SSE stream / active-row highlight doesn't point at a dead session.
  if (activeSessionDeleted) {
    next.setActiveSession(activeTab?.destination === 'chat' ? activeTab.resourceId : null);
  }
}
